import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DatePicker } from '@/components/ui/date-picker';
import { Clock, Loader2, Settings, CalendarDays, ListTodo, ArrowLeft, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import {
  bookingService,
  type WeeklySchedule,
  type ScheduleException,
  type BookingSettings,
} from '@/services/bookingService';
import {
  WeeklyAvailability,
  type DaySchedule,
  BookingLinkCard,
  BookingSettingsForm,
  ScheduleExceptionsList,
  TimezoneSelector,
} from '@/components/booking';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '@/contexts/AuthContext';
import AppointmentTypeTab from '@/components/booking/AppointmentTypeTab';

// Exception types
const EXCEPTION_TYPES = [
  { value: 'time_off', label: 'Day Off' },
  { value: 'custom_hours', label: 'Custom Hours' },
  { value: 'date_range_unavailable', label: 'Block Date Range' },
];

interface AppointmentTypeFormData {
  name: string;
  description: string;
  duration_minutes: number;
  price_cents: number | null;
  color: string;
  telehealth_enabled: boolean;
  in_person_enabled: boolean;
  is_public: boolean;
}

interface ExceptionFormData {
  exception_date: Date | undefined;
  end_date: Date | undefined;
  exception_type: 'time_off' | 'custom_hours' | 'date_range_unavailable';
  is_all_day: boolean;
  start_time: string;
  end_time: string;
  notes: string;
}

const defaultExceptionForm: ExceptionFormData = {
  exception_date: undefined,
  end_date: undefined,
  exception_type: 'time_off',
  is_all_day: true,
  start_time: '09:00',
  end_time: '17:00',
  notes: '',
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (!error || typeof error !== 'object') return fallback;

  const typedError = error as {
    message?: string;
    error?: { message?: string };
    response?: { data?: { message?: string; error?: { message?: string } } };
  };

  return (
    typedError.message ||
    typedError.error?.message ||
    typedError.response?.data?.error?.message ||
    typedError.response?.data?.message ||
    fallback
  );
};

// Sidebar navigation items
const SIDEBAR_ITEMS = [
  { id: 'availability', label: 'Weekly Availability', icon: Clock },
  { id: 'appointment-types', label: 'Appointment Types', icon: ListTodo },
  { id: 'exceptions', label: 'Schedule Exceptions', icon: CalendarDays },
  { id: 'settings', label: 'Booking Settings', icon: Settings },
];

const BookingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { clinic } = useClinic();
  const { user } = useAuth();

  // Active tab
  const [activeTab, setActiveTab] = useState('availability');

  // Loading and saving states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Data states
  const [schedule, setSchedule] = useState<DaySchedule[]>([]);
  const [exceptions, setExceptions] = useState<ScheduleException[]>([]);
  const [settings, setSettings] = useState<BookingSettings | null>(null);
  // V2 (identity-first / OTP-verified) default booking URL — points at the
  // first appointment type via clinic+type slugs. Surfaced in the banner so
  // the link a clinician copies sends patients into the new flow.
  const [v2DefaultUrl, setV2DefaultUrl] = useState<string>('');
  const [timezone, setTimezone] = useState(() => {
    // Auto-detect user's timezone from browser
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';
    } catch {
      return 'America/Chicago';
    }
  });

  // Exception Dialog State
  const [exceptionDialogOpen, setExceptionDialogOpen] = useState(false);
  const [exceptionForm, setExceptionForm] = useState<ExceptionFormData>(defaultExceptionForm);

  // Convert API schedule format to component format
  const convertToComponentSchedule = (apiSchedule: WeeklySchedule[]): DaySchedule[] => {
    const result: DaySchedule[] = [];

    // Group by day
    const byDay = new Map<number, WeeklySchedule[]>();
    apiSchedule.forEach(s => {
      if (!byDay.has(s.day_of_week)) {
        byDay.set(s.day_of_week, []);
      }
      byDay.get(s.day_of_week)!.push(s);
    });

    byDay.forEach((schedules, day) => {
      result.push({
        day_of_week: day,
        is_available: schedules.some(s => s.is_available),
        time_ranges: schedules.map(s => ({
          id: s.id || uuidv4(),
          start_time: s.start_time,
          end_time: s.end_time,
        })),
      });
    });

    return result;
  };

  // Convert component schedule format to API format
  const convertToApiSchedule = (componentSchedule: DaySchedule[]): WeeklySchedule[] => {
    const result: WeeklySchedule[] = [];

    componentSchedule.forEach(day => {
      if (day.is_available) {
        day.time_ranges.forEach(range => {
          result.push({
            day_of_week: day.day_of_week,
            start_time: range.start_time,
            end_time: range.end_time,
            is_available: true,
          });
        });
      } else {
        // Include unavailable days with default times
        result.push({
          day_of_week: day.day_of_week,
          start_time: '09:00:00',
          end_time: '17:00:00',
          is_available: false,
        });
      }
    });

    return result;
  };

  // Load data
  const loadData = useCallback(async () => {
    if (!clinic?.id) return;

    setLoading(true);
    try {
      const [availabilityData, settingsData, exceptionsData, v2Links] = await Promise.all([
        bookingService.getAvailability(clinic.id),
        bookingService.getSettings(clinic.id),
        bookingService.getExceptions(clinic.id),
        bookingService.getV2BookingLinks(clinic.id),
      ]);

      setSchedule(convertToComponentSchedule(availabilityData));
      setSettings(settingsData);
      setExceptions(exceptionsData);

      if (settingsData?.timezone) {
        setTimezone(settingsData.timezone);
      }

      // Prefer the practitioner-scoped URL (auto-preselects the provider
      // so V2 skips the chooser). Clinic admin/staff get the clinic-wide
      // default instead.
      const preferredUrl = v2Links?.practitioner_default_url || v2Links?.default_url;
      if (preferredUrl) {
        setV2DefaultUrl(preferredUrl);
      }
    } catch (error) {
      console.error('Failed to load booking data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load booking settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [clinic?.id, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Save schedule
  const handleSaveSchedule = async () => {
    if (!clinic?.id) return;

    setSaving(true);
    try {
      await bookingService.updateAvailability(clinic.id, convertToApiSchedule(schedule), timezone);
      toast({
        title: 'Success',
        description: 'Availability schedule saved',
      });
    } catch (error) {
      console.error('Failed to save schedule:', error);
      toast({
        title: 'Error',
        description: 'Failed to save availability schedule',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    if (!clinic?.id || !settings) return;

    setSaving(true);
    try {
      await bookingService.updateSettings(clinic.id, settings);
      toast({
        title: 'Success',
        description: 'Booking settings saved',
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save booking settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Open add exception dialog
  const handleAddException = () => {
    setExceptionForm(defaultExceptionForm);
    setExceptionDialogOpen(true);
  };

  // Save exception
  const handleSaveException = async () => {
    if (!clinic?.id) return;

    const isRange = exceptionForm.exception_type === 'date_range_unavailable';

    if (isRange) {
      if (!exceptionForm.exception_date || !exceptionForm.end_date) {
        toast({
          title: 'Validation Error',
          description: 'Please select both a start date and an end date.',
          variant: 'destructive',
        });
        return;
      }
      if (exceptionForm.exception_date > exceptionForm.end_date) {
        toast({
          title: 'Validation Error',
          description: 'Start date must be on or before end date.',
          variant: 'destructive',
        });
        return;
      }
    } else if (!exceptionForm.exception_date) {
      toast({
        title: 'Validation Error',
        description: 'Please select a date for the exception.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      if (isRange) {
        const result = await bookingService.createDateRangeException(clinic.id, {
          start_date: format(exceptionForm.exception_date!, 'yyyy-MM-dd'),
          end_date: format(exceptionForm.end_date!, 'yyyy-MM-dd'),
          notes: exceptionForm.notes || undefined,
        });
        const parts = [`${result.created_count} day(s) marked unavailable`];
        if (result.skipped_count > 0) {
          parts.push(`${result.skipped_count} skipped (already had exceptions)`);
        }
        toast({ title: 'Success', description: parts.join('. ') });
      } else {
        await bookingService.createException(clinic.id, {
          exception_date: format(exceptionForm.exception_date!, 'yyyy-MM-dd'),
          exception_type: exceptionForm.exception_type as 'time_off' | 'custom_hours',
          is_all_day: exceptionForm.is_all_day,
          start_time:
            exceptionForm.exception_type === 'custom_hours' ? exceptionForm.start_time : undefined,
          end_time:
            exceptionForm.exception_type === 'custom_hours' ? exceptionForm.end_time : undefined,
          notes: exceptionForm.notes || undefined,
        });
        toast({ title: 'Success', description: 'Schedule exception added' });
      }
      setExceptionDialogOpen(false);
      loadData();
    } catch (error: any) {
      console.error('Failed to create exception:', error);
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to add exception'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Delete exception
  const handleDeleteException = async (exceptionId: string) => {
    if (!clinic?.id) return;

    try {
      await bookingService.deleteException(clinic.id, exceptionId);
      loadData();
    } catch (error: unknown) {
      console.error('Failed to delete exception:', error);
      throw new Error(getErrorMessage(error, 'Failed to remove exception'));
    }
  };

  // Delete exception group (date range)
  const handleDeleteExceptionGroup = async (groupId: string) => {
    if (!clinic?.id) return;

    try {
      await bookingService.deleteExceptionGroup(clinic.id, groupId);
      loadData();
    } catch (error: unknown) {
      console.error('Failed to delete exception group:', error);
      throw new Error(getErrorMessage(error, 'Failed to remove blocked date range'));
    }
  };

  // Copy booking link — V2 only. V1 is deprecated; the V1 public route
  // auto-redirects to V2, but admin surfaces never hand out a V1 URL.
  const handleCopyBookingLink = () => {
    if (!v2DefaultUrl) return;
    navigator.clipboard.writeText(v2DefaultUrl);
    toast({
      title: 'Copied!',
      description: 'Booking link copied to clipboard',
    });
  };

  if (loading) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-brand-teal' />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-slate-50'>
      <div className='mx-auto max-w-7xl p-6'>
        {/* Header with Back Button */}
        <div className='mb-8'>
          <div className='mb-2 flex items-center gap-4'>
            <Button variant='ghost' size='icon' onClick={() => navigate(-1)} className='h-9 w-9'>
              <ArrowLeft className='h-5 w-5' />
            </Button>
            <h1 className='font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl'>Booking & Availability</h1>
          </div>
          <p className='ml-13 text-sm text-slate-500'>
            Manage your schedule, appointment types, and booking settings
          </p>
        </div>

        {/* Booking Link Banner — V2 only. Legacy V1 link is not surfaced;
            old V1 URLs already shared with patients are redirected to V2
            by the public booking page. */}
        {v2DefaultUrl && (
          <BookingLinkCard
            bookingUrl={v2DefaultUrl}
            onCopy={handleCopyBookingLink}
            className='mb-6'
          />
        )}

        {/* Main Content with Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className='space-y-6'>
          <TabsList className='h-auto flex-wrap border bg-white p-1 shadow-sm'>
            {SIDEBAR_ITEMS.map(item => (
              <TabsTrigger
                key={item.id}
                value={item.id}
                className='flex items-center gap-2 px-4 py-2 data-[state=active]:bg-brand-teal/10 data-[state=active]:text-brand-teal'
              >
                <item.icon className='h-4 w-4' />
                <span className='hidden sm:inline'>{item.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Weekly Availability Tab */}
          <TabsContent value='availability' className='space-y-6'>
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Clock className='h-5 w-5 text-brand-teal' />
                  Weekly Availability
                </CardTitle>
                <CardDescription>
                  Set your recurring weekly schedule. Add multiple time ranges for split shifts.
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-6'>
                {/* Timezone Selector */}
                <TimezoneSelector value={timezone} onChange={setTimezone} disabled={saving} />

                <Separator />

                {/* Weekly Schedule */}
                <WeeklyAvailability schedule={schedule} onChange={setSchedule} disabled={saving} />

                <div className='flex justify-end gap-3 pt-4'>
                  <Button variant='outline' onClick={loadData} disabled={saving}>
                    Reset
                  </Button>
                  <Button
                    onClick={handleSaveSchedule}
                    disabled={saving}
                    className='bg-brand-teal hover:bg-brand-teal/90'
                  >
                    {saving ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
                    Save Availability
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appointment Types Tab */}
          <TabsContent value='appointment-types' className='space-y-6'>
            <AppointmentTypeTab />
          </TabsContent>

          {/* Exceptions Tab */}
          <TabsContent value='exceptions' className='space-y-6'>
            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <div>
                    <CardTitle className='flex items-center gap-2'>
                      <CalendarDays className='h-5 w-5 text-brand-teal' />
                      Schedule Exceptions
                    </CardTitle>
                    <CardDescription>
                      Block time off or set custom hours for specific dates
                    </CardDescription>
                  </div>
                  <Button variant='outline' onClick={handleAddException}>
                    <Plus className='mr-2 h-4 w-4' />
                    Add Exception
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScheduleExceptionsList
                  exceptions={exceptions}
                  onAdd={handleAddException}
                  onDelete={handleDeleteException}
                  onDeleteGroup={handleDeleteExceptionGroup}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value='settings' className='space-y-6'>
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Settings className='h-5 w-5 text-brand-teal' />
                  Booking Settings
                </CardTitle>
                <CardDescription>Configure booking policies and notifications</CardDescription>
              </CardHeader>
              <CardContent className='space-y-6'>
                <BookingSettingsForm settings={settings} onChange={setSettings} disabled={saving} />

                <div className='flex justify-end gap-3 pt-4'>
                  <Button variant='outline' onClick={loadData} disabled={saving}>
                    Reset
                  </Button>
                  <Button
                    onClick={handleSaveSettings}
                    disabled={saving}
                    className='bg-brand-teal hover:bg-brand-teal/90'
                  >
                    {saving ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Exception Dialog */}
        <Dialog open={exceptionDialogOpen} onOpenChange={setExceptionDialogOpen}>
          <DialogContent className='max-h-[90vh] max-w-lg overflow-y-auto'>
            <DialogHeader>
              <DialogTitle>Add Schedule Exception</DialogTitle>
              <DialogDescription>
                Block time off, set custom hours, or block a date range
              </DialogDescription>
            </DialogHeader>

            <div className='space-y-4 py-4'>
              {/* Exception Type */}
              <div className='space-y-2'>
                <Label>Exception Type</Label>
                <Select
                  value={exceptionForm.exception_type}
                  onValueChange={v =>
                    setExceptionForm({
                      ...exceptionForm,
                      exception_type: v as ExceptionFormData['exception_type'],
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select type...' />
                  </SelectTrigger>
                  <SelectContent>
                    {EXCEPTION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Picker(s) */}
              {exceptionForm.exception_type === 'date_range_unavailable' ? (
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label>Start Date *</Label>
                    <DatePicker
                      value={exceptionForm.exception_date}
                      onValueChange={date =>
                        setExceptionForm({ ...exceptionForm, exception_date: date })
                      }
                      minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                      placeholder='MM/DD/YYYY'
                      disabled={saving}
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label>End Date *</Label>
                    <DatePicker
                      value={exceptionForm.end_date}
                      onValueChange={date =>
                        setExceptionForm({ ...exceptionForm, end_date: date })
                      }
                      minDate={exceptionForm.exception_date ?? new Date(new Date().setHours(0, 0, 0, 0))}
                      placeholder='MM/DD/YYYY'
                      disabled={saving}
                    />
                  </div>
                </div>
              ) : (
                <div className='space-y-2'>
                  <Label>Date *</Label>
                  <DatePicker
                    value={exceptionForm.exception_date}
                    onValueChange={date =>
                      setExceptionForm({ ...exceptionForm, exception_date: date })
                    }
                    minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                    placeholder='MM/DD/YYYY'
                    disabled={saving}
                  />
                </div>
              )}

              {/* Custom Hours - Only show for custom_hours type */}
              {exceptionForm.exception_type === 'custom_hours' && (
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label>Start Time</Label>
                    <Input
                      type='time'
                      value={exceptionForm.start_time}
                      onChange={e =>
                        setExceptionForm({ ...exceptionForm, start_time: e.target.value })
                      }
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label>End Time</Label>
                    <Input
                      type='time'
                      value={exceptionForm.end_time}
                      onChange={e =>
                        setExceptionForm({ ...exceptionForm, end_time: e.target.value })
                      }
                    />
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className='space-y-2'>
                <Label htmlFor='exception-notes'>Notes (optional)</Label>
                <Textarea
                  id='exception-notes'
                  value={exceptionForm.notes}
                  onChange={e => setExceptionForm({ ...exceptionForm, notes: e.target.value })}
                  placeholder="e.g., Doctor's appointment, Holiday, etc."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant='outline' onClick={() => setExceptionDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveException}
                disabled={saving || !exceptionForm.exception_date}
                className='bg-brand-teal hover:bg-brand-teal/90'
              >
                {saving ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
                Add Exception
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default BookingDashboard;
